import { isNotDefined, getSourcePathAccessor } from "../utils";
import { SMA as defaultOptions } from "./defaultOptionsForComputation";

/**
 * TSMA - Time Series Moving Average (Linear Regression Moving Average)
 *
 * Calculates the endpoint of a linear regression trendline over N periods.
 * Also known as "Linear Regression Moving Average" or "Endpoint Moving Average."
 *
 * This is more responsive to trend changes than SMA and follows the direction
 * of the linear regression line.
 *
 * Formula:
 * For N periods, fit linear regression: y = a + b*x
 * - b (slope) = (N*sumWeightedCloses - sumWeights*sumCloses) / (N*sumWeightsSquared - squaredSumWeights)
 * - a (intercept) = (sumCloses - b*sumWeights) / N
 * - TSMA = a + b*N (the forecast/endpoint value)
 */

export default function () {
  let options = defaultOptions;

  function calculator(data) {
    const { windowSize, sourcePath } = options;
    const source = getSourcePathAccessor(sourcePath);

    // Pre-calculate constants for linear regression
    const sumWeights = (windowSize * (windowSize + 1)) / 2;
    const squaredSumWeights = Math.pow(sumWeights, 2);
    const sumWeightsSquared = (sumWeights * (2 * windowSize + 1)) / 3;

    return data.map((d, i) => {
      // Need at least windowSize bars
      if (i < windowSize - 1) {
        return undefined;
      }

      // Collect values for the regression window
      let sumCloses = 0;
      let sumWeightedCloses = 0;
      let validCount = 0;

      for (let j = 0; j < windowSize; j++) {
        const val = source(data[i - j]);
        if (isNotDefined(val)) {
          return undefined; // Skip if any value in window is missing
        }
        // Weight is the position (1 for oldest, windowSize for newest)
        const weight = windowSize - j;
        sumCloses += val;
        sumWeightedCloses += weight * val;
        validCount++;
      }

      // Need full window
      if (validCount !== windowSize) {
        return undefined;
      }

      // Calculate linear regression parameters
      // Slope: b = (N*sumWeightedCloses - sumWeights*sumCloses) / (N*sumWeightsSquared - squaredSumWeights)
      const b =
        (windowSize * sumWeightedCloses - sumWeights * sumCloses) /
        (windowSize * sumWeightsSquared - squaredSumWeights);

      // Intercept: a = (sumCloses - b*sumWeights) / N
      const a = (sumCloses - b * sumWeights) / windowSize;

      // TSMA is the forecast at the endpoint: a + b*N
      const tsma = a + b * windowSize;

      return tsma;
    });
  }

  calculator.undefinedLength = function () {
    const { windowSize } = options;
    return windowSize - 1;
  };

  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  return calculator;
}
